import React, { useRef, useEffect, useCallback, useState } from 'react'
import { GoogleMap, OverlayView, } from '@react-google-maps/api'
import { ArrowLeft, ArrowRight, Home, PersonStanding, ShoppingBag, Store, User, X } from 'lucide-react';
import StoreCard from '../StoreCard/StoreCard';
import { useLocationContext } from '../../../context/LocationContext';

const options /*:google.maps.MapOptions */ = {
  disableDefaultUI: true,
  zoomControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  clickableIcons: false,
};

const overlayViewDivStyle = {
  transform: "translate(-50%, -100%)", // center the icon above point
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}

const arrowStyle = (bg, makeSmall = false) => {
  const borderX = makeSmall ? "7px" : "14px";
  const borderTop = makeSmall ? "10px" : "20px";
  const marginTop = makeSmall ? "-4px" : "-8px";
  return {
    width: 0,
    height: 0,
    borderLeft: `${borderX} solid transparent`,
    borderRight: `${borderX} solid transparent`,
    borderTop: `${borderTop} solid ${bg}`,
    marginTop,
    zindex: 5,
    opacity: makeSmall ? 0.7 : 1,
  }
}

const pinProps = (isStore = false, isActive = false, hasProducts) => ({
  size: 32,
  strokeWidth: 2.5,
  className: `rounded-full p-2 z-50 ${((isActive) || !isStore) ? 'bg-gg-green stroke-gg-dark' :
    (hasProducts ? 'bg-gg-dark stroke-gg-green' : 'bg-[#aaa] stroke-gg-dark'
    )
    }`,
  zindex: 10,
  opacity: !isStore || isActive || hasProducts ? 1 : 1,
})



const GoogleMapComponent = ({
  mapContainerStyle = { width: '100%', height: 'calc(100vh - 4.4em)', top: 0 },
  center = {
    lat: -3.745,
    lng: -38.523,
  },
  locationsArray = [],
  activeStore = null,
  setActiveStore = () => { },

}) => {


  const { coords } = useLocationContext();
  const mapRef = useRef(null);


  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);


  // Fit to center + all store locations whenever they change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!locationsArray || !locationsArray.length) return;
    fitToBounds(map, locationsArray);
  }, [center, locationsArray]);


  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (activeStore) {
      const store = locationsArray.find((s) => s.id === activeStore);
      if (store) {

        map.panTo({ lat: store.lat, lng: store.lng }, { duration: 500 });
        map.setZoom(18);
      }
    } else {
      fitToBounds(map, locationsArray);
    }

  }, [activeStore])

  const fitToBounds = (map, locations) => {
    // filter bad points
    const points =
      locations
        .filter(
          (p) =>
            Number.isFinite(p.lat) &&
            Number.isFinite(p.lng)
        )
        .map(({ lat, lng }) => ({ lat, lng }))


    if (!points.length) return;

    if (points.length === 1) {
      // single point: set a sensible zoom
      map.setCenter(points[0]);
      map.setZoom(15);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));

    try {
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    } catch {
      console.log("Error fitting bounds, likely due to invalid coordinates.");
    }

  };

  let hasMore = false;
  let hasLess = false;
  if (locationsArray.length > 1) {
    hasMore = true;
    if (locationsArray.length > 2) {
      hasLess = true;
    }
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={13}
      onLoad={onLoad}
      onUnmount={onUnmount}
      mapContainerClassName='w-full'
      controls={false}
      options={options}
    >


      {coords ?
        <OverlayView
          position={{ lat: coords.latitude, lng: coords.longitude }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div style={overlayViewDivStyle}>
            <User {...pinProps(false)} />
            <div style={arrowStyle("#00CB77")} />
          </div>
        </OverlayView>
        : <></>
      }

      {locationsArray.map((loc) => {
        const isActive = activeStore === String(loc.id);
        return (
          <OverlayView
            key={loc.id}
            position={{ lat: loc.lat, lng: loc.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}

          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                setActiveStore(isActive ? null : loc.id);
              }}
              style={overlayViewDivStyle}
              className="focus:outline-none"
            >
              <ShoppingBag {...pinProps(true, isActive, loc.hasProducts)} />
              <div style={arrowStyle(isActive ? "#00CB77" : (loc.hasProducts ? "#004C42" : "#aaa"))} />
            </div>
          </OverlayView>
        )
      })}

      {
        // THIS IS REALLY DUSTY LOGIC CARL AND IT NEEDS REFEACTORED
        activeStore && locationsArray.find((s) => s.id === activeStore) ? (
          <>

            <a onClick={(e) => {
              e.stopPropagation
              setActiveStore(null)
            }}
              className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center bg-gg-dark px-2 py-1 shadow-md rounded text-white font-gg-medium text-sm z-50">
              See all
            </a>

            <div className="absolute bottom-0  mb-4  flex justify-between items-center w-full">
              <a
                onClick={() => {
                  if (!hasLess)
                    return;
                  const indexOfActive = locationsArray.findIndex(s => s.id === activeStore);
                  const lessStore = locationsArray[indexOfActive - 1];
                  if (lessStore) {
                    setActiveStore(lessStore.id);
                  }
                  else {
                    setActiveStore(locationsArray[locationsArray.length - 1].id);
                  }
                }}
                className={"flex items-center bg-gg-dark px-1 h-12 shadow-md rounded-br rounded-tr " + (hasLess ? '' : 'opacity-0')}
              >
                <ArrowLeft size={16} className='stroke-white' />
              </a>
              <div className="bg-white w-10/12 rounded-md px-2 py-1 shadow-lg">
                <StoreCard
                  {...locationsArray.find((s) => s.id === activeStore)} isMap={true} />
              </div>
              <a
                onClick={() => {
                  if (!hasMore)
                    return;
                  const indexOfActive = locationsArray.findIndex(s => s.id === activeStore);
                  const nextStore = locationsArray[indexOfActive + 1];
                  if (nextStore) {
                    setActiveStore(nextStore.id);
                  }
                  else {
                    setActiveStore(locationsArray[0].id);
                  }
                }}
                className={"flex items-center bg-gg-dark px-1 h-12 shadow-lg rounded-bl rounded-tl " + (hasMore ? '' : ' opacity-0')}
              >
                <ArrowRight size={16} className='stroke-white' />
              </a>
            </div>
          </>
        ) : <></>

      }
    </GoogleMap >
  )

}

export default GoogleMapComponent